import { useEffect, useState, useCallback } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import PreflightPanel from "@/components/app/PreflightPanel";
import AssetHealthTab from "@/components/app/AssetHealthTab";
import { AssetWarrantyPanel } from "@/pages/warranty/WarrantyCoverage";
import AssetLifecycleCard from "@/components/app/AssetLifecycleCard";
import { HeaderSummary, ActionBar, HealthCategorySection, ComplianceSection, TicketsSection, RepairHistorySection, AssignmentHistorySection, LifecycleSection, FinancialSection, BuybackSection, DocumentsSection, AuditTrailSection, Section } from "@/components/app/AssetDrawerSections";
import RecordStickyNotes from "@/components/app/RecordStickyNotes";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { X, Pencil, Save, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import StatusBadge from "./StatusBadge";
import Timeline from "./Timeline";
import { ASSET } from "@/constants/testIds";

function Field({ label, value, testId }) {
  return (
    <div>
      <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500">
        {label}
      </div>
      <div className="text-sm text-slate-900 mt-0.5" data-testid={testId}>{value || "—"}</div>
    </div>
  );
}

function EditableField({ label, value, onChange, placeholder, testId, type = "text", multiline = false, className = "" }) {
  return (
    <div className={className}>
      <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500 mb-1">
        {label}
      </div>
      {multiline ? (
        <textarea rows={4} value={value ?? ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} data-testid={testId}
          className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-slate-300" />
      ) : (
      <Input
        type={type}
        value={value ?? ""}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="h-8 text-sm"
      />
      )}
    </div>
  );
}

const SPEC_FIELDS = [["brand", "Brand"], ["category", "Category"], ["model", "Model"], ["sku", "SKU"], ["os", "OS"], ["colour", "Colour"], ["processor", "Processor"], ["ram", "RAM"], ["storage", "Storage"], ["imei1", "IMEI 1"], ["imei2", "IMEI 2"]];

function EditableSelect({ label, value, onChange, options, testId }) {
  return (
    <div>
      <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500 mb-1">
        {label}
      </div>
      <Select value={value ?? ""} onValueChange={(v) => onChange(v)}>
        <SelectTrigger className="h-8 text-sm" data-testid={testId}>
          <SelectValue placeholder="—" />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o} value={o}>{o}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

const LIFECYCLE_STATES = ["in_stock", "in_warehouse", "assigned", "in_use", "in_transit",
                            "returned", "under_repair", "retired", "deactivated"];
const CONDITIONS = ["A", "B", "C", "damaged", "new"];
const MDM_STATES = ["pending", "enrolled", "removed", "not_applicable"];
const ENROLLMENT_STATES = ["pending", "verified", "failed"];
const ERASURE_STATES = ["not_required", "pending", "in_progress", "certified", "failed"];

export default function AssetDrawer({ assetId, open, onOpenChange, onChanged }) {
  const [asset, setAsset] = useState(null);
  const [events, setEvents] = useState([]);
  const [history, setHistory] = useState({});
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [x360, setX360] = useState(null);
  const [tab, setTab] = useState("overview");

  const load360 = useCallback(() => api.get(`/assets/${assetId}/360`).then((r) => setX360(r.data)).catch(() => setX360(null)), [assetId]);

  useEffect(() => {
    if (!open || !assetId) return;
    let cancelled = false;
    setLoading(true);
    setEditing(false);
    setX360(null);
    setTab("overview");
    load360();
    Promise.all([
      api.get(`/assets/${assetId}`),
      api.get(`/assets/${assetId}/timeline`),
      api.get(`/assets/${assetId}/history`),
    ])
      .then(([a, t, h]) => {
        if (cancelled) return;
        setAsset(a.data);
        setEvents(t.data.items || []);
        setHistory(h.data || {});
      })
      .catch((e) => toast.error(e.response?.data?.detail || "Failed to load asset"))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [assetId, load360, open]);

  const startEdit = () => {
    setDraft({
      asset_tag: asset.asset_tag || "",
      serial: asset.serial || "",
      lifecycle_state: asset.lifecycle_state || "",
      condition: asset.condition || "",
      mdm_status: asset.mdm_status || "",
      enrollment_status: asset.enrollment_status || "",
      data_erasure_status: asset.data_erasure_status || "",
      legal_hold: !!asset.legal_hold,
      notes: asset.notes || "",
      // Specifications
      ...Object.fromEntries([...SPEC_FIELDS.map(([k]) => k), "config"].map((k) => [k, asset[k] || ""])),
      // Purchase + warranty inline edits
      purchase_date: asset.purchase_date || "",
      po_number: asset.po_number || "",
      invoice_number: asset.invoice_number || "",
      invoice_date: asset.invoice_date || "",
      purchase_price: asset.purchase_price ?? "",
      currency: asset.currency || "",
      supplier: asset.supplier || "",
      warranty_start: asset.warranty_start || "",
      warranty_end: asset.warranty_end || "",
      applecare_end: asset.applecare_end || "",
      abm_id: asset.abm_id || "",
      change_reason: "",
    });
    setEditing(true);
  };

  const cancelEdit = () => { setEditing(false); setDraft({}); };

  const save = async () => {
    // Only send changed fields; strip empties for optional strings
    const patch = {};
    const STRING_FIELDS = ["asset_tag", "serial", "lifecycle_state", "condition",
                            "mdm_status", "enrollment_status", "data_erasure_status",
                            "notes", "purchase_date", "po_number", "invoice_number",
                            "invoice_date", "currency", "supplier",
                            "warranty_start", "warranty_end", "applecare_end",
                            "abm_id", "config", ...SPEC_FIELDS.map(([k]) => k)];
    for (const k of STRING_FIELDS) {
      if ((draft[k] ?? "") !== (asset[k] ?? "")) patch[k] = draft[k] || null;
    }
    // Numeric — purchase_price
    const draftPrice = draft.purchase_price === "" || draft.purchase_price == null
      ? null : Number(draft.purchase_price);
    if (draftPrice !== (asset.purchase_price ?? null)) {
      if (draftPrice != null && Number.isNaN(draftPrice)) {
        toast.error("Purchase price must be a number.");
        return;
      }
      patch.purchase_price = draftPrice;
    }
    if (!!draft.legal_hold !== !!asset.legal_hold) patch.legal_hold = !!draft.legal_hold;

    // Serial / Tag corrections MUST carry a reason — the server enforces
    // it too but surface the requirement early so operators aren't
    // surprised by a 422.
    const identifierChanged = ("serial" in patch) || ("asset_tag" in patch);
    if (identifierChanged) {
      if (!draft.change_reason?.trim()) {
        toast.error("Please add a reason — required for Serial/Tag corrections.");
        return;
      }
      patch.change_reason = draft.change_reason.trim();
    }

    if (Object.keys(patch).length === 0) {
      toast.info("Nothing to save.");
      setEditing(false);
      return;
    }

    setSaving(true);
    try {
      const { data } = await api.patch(`/assets/${assetId}`, patch);
      setAsset(data.ok ? { ...asset, ...patch } : data);
      toast.success("Asset updated");
      setEditing(false);
      setDraft({});
      onChanged?.();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (typeof detail === "object" && detail?.error === "duplicate_serial") {
        toast.error(detail.message || "Another asset already carries that serial.");
      } else if (typeof detail === "object" && detail?.error === "change_reason_required") {
        toast.error(detail.message || "Reason required for identifier corrections.");
      } else {
        toast.error(typeof detail === "string" ? detail : "Could not save asset");
      }
    } finally { setSaving(false); }
  };

  const setField = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const isDisposed = ["disposed", "destroyed", "recycled"].includes(asset?.lifecycle_state);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        data-testid={ASSET.drawer}
        className="w-full max-w-[100vw] sm:max-w-4xl overflow-y-auto overflow-x-hidden p-0"
      >
        <SheetHeader className="px-6 py-4 border-b border-slate-200 sticky top-0 bg-white z-10">
          <div className="flex items-start justify-between gap-2 sm:gap-4">
            <div className="min-w-0 flex-1">
              <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500">
                Asset 360{editing && " · Editing"}
              </div>
              <SheetTitle className="text-lg font-heading font-semibold text-slate-900 mt-0.5">
                {asset ? (x360?.header?.asset_name || `${asset.brand || ""} ${asset.model || ""}`.trim() || asset.asset_tag) : "Loading..."}
              </SheetTitle>
              {asset ? (
                <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                  <span className="font-mono">{asset.asset_tag}</span>
                  <span>·</span>
                  <span className="font-mono">{asset.serial}</span>
                  <StatusBadge value={asset.lifecycle_state} />
                  {asset.serial && <a href={`/warehouse/returns?tab=returns`} onClick={() => navigator.clipboard?.writeText(asset.serial)} className="font-semibold text-teal-700 underline" title="Opens Returns & Redeployment; serial copied for scanning" data-testid="asset-drawer-returns-link">Returns &amp; Redeployment</a>}
                  <StatusBadge value={asset.condition} />
                </div>
              ) : null}
            </div>
            <div className="flex shrink-0 items-center gap-1">
              {asset && !editing && !isDisposed && (
                <Button size="sm" variant="outline" onClick={startEdit}
                  data-testid="asset-drawer-edit">
                  <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                </Button>
              )}
              {editing && (
                <>
                  <Button size="sm" variant="ghost" onClick={cancelEdit}
                    disabled={saving} data-testid="asset-drawer-cancel">
                    Cancel
                  </Button>
                  <Button size="sm" onClick={save} disabled={saving}
                    data-testid="asset-drawer-save">
                    {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                            : <Save className="h-3.5 w-3.5 mr-1" />}
                    Save
                  </Button>
                </>
              )}
              <Button
                data-testid={ASSET.drawerClose}
                variant="ghost"
                size="icon"
                onClick={() => onOpenChange(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </SheetHeader>

        {loading || !asset ? (
          <div className="p-6 text-sm text-slate-500">Loading asset details…</div>
        ) : (
          <div className="px-6 py-4">
            {x360 && (
              <div className="mb-4 space-y-3" data-testid="asset-360-top">
                <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-4">
                  <HeaderSummary h={x360.header} />
                </div>
                <ActionBar
                  assetId={asset.id}
                  actions={x360.actions}
                  onDone={(key) => {
                    load360();
                    api.get(`/assets/${assetId}`).then((r) => setAsset(r.data)).catch(() => {});
                    api.get(`/assets/${assetId}/timeline`).then((r) => setEvents(r.data.items || [])).catch(() => {});
                    onChanged?.();
                    if (key === "diagnose") setTab("health");
                    else if (["raise_ticket", "remote_support", "replace", "recover"].includes(key)) setTab("tickets");
                    else if (key === "repair") setTab("repairs");
                    else if (key === "buyback") setTab("buyback");
                    else setTab("lifecycle");
                  }}
                />
              </div>
            )}
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1 bg-slate-100/80 p-1" data-testid="asset-tabs">
                <TabsTrigger value="overview" data-testid={ASSET.overviewTab}>Device Overview</TabsTrigger>
                <TabsTrigger value="health" data-testid="asset-health-tab">Health</TabsTrigger>
                <TabsTrigger value="hardware" data-testid="asset-hardware-tab">Hardware Health</TabsTrigger>
                <TabsTrigger value="software" data-testid="asset-software-tab">Software</TabsTrigger>
                <TabsTrigger value="security" data-testid="asset-security-tab">Security</TabsTrigger>
                <TabsTrigger value="compliance" data-testid="asset-compliance-tab">Compliance</TabsTrigger>
                <TabsTrigger value="specs">Specs</TabsTrigger>
                <TabsTrigger value="warranty" data-testid="asset-warranty-tab">Warranty</TabsTrigger>
                <TabsTrigger value="tickets" data-testid="asset-tickets-tab">Tickets</TabsTrigger>
                <TabsTrigger value="repairs" data-testid="asset-repairs-tab">Repair History</TabsTrigger>
                <TabsTrigger value="assignments" data-testid="asset-assignments-tab">Assignment History</TabsTrigger>
                <TabsTrigger value="lifecycle" data-testid="asset-lifecycle-tab">Lifecycle</TabsTrigger>
                <TabsTrigger value="financial" data-testid="asset-financial-tab">Financial Information</TabsTrigger>
                <TabsTrigger value="buyback" data-testid="asset-buyback-tab">Buyback / Residual Value</TabsTrigger>
                <TabsTrigger value="documents" data-testid="asset-documents-tab">Documents</TabsTrigger>
                <TabsTrigger value="audit" data-testid="asset-audit-tab">Audit Trail</TabsTrigger>
                <TabsTrigger value="history" data-testid={ASSET.historyTab}>History</TabsTrigger>
                <TabsTrigger value="timeline" data-testid={ASSET.timelineTab}>Timeline</TabsTrigger>
                <TabsTrigger value="preflight" data-testid="asset-preflight-tab">Preflight</TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="mt-4 grid grid-cols-2 gap-4">
                {editing ? (
                  <>
                    <EditableField label="Asset Tag" value={draft.asset_tag}
                      onChange={(v) => setField("asset_tag", v)}
                      testId="edit-asset-tag" />
                    <EditableField label="Serial" value={draft.serial}
                      onChange={(v) => setField("serial", v)}
                      testId="edit-serial" />
                    <EditableSelect label="Lifecycle state" value={draft.lifecycle_state}
                      onChange={(v) => setField("lifecycle_state", v)}
                      options={LIFECYCLE_STATES} testId="edit-lifecycle-state" />
                    <EditableSelect label="Condition" value={draft.condition}
                      onChange={(v) => setField("condition", v)}
                      options={CONDITIONS} testId="edit-condition" />
                    <EditableSelect label="MDM status" value={draft.mdm_status}
                      onChange={(v) => setField("mdm_status", v)}
                      options={MDM_STATES} testId="edit-mdm-status" />
                    <EditableSelect label="Enrollment" value={draft.enrollment_status}
                      onChange={(v) => setField("enrollment_status", v)}
                      options={ENROLLMENT_STATES} testId="edit-enrollment-status" />
                    <EditableSelect label="Data erasure" value={draft.data_erasure_status}
                      onChange={(v) => setField("data_erasure_status", v)}
                      options={ERASURE_STATES} testId="edit-data-erasure-status" />
                    <div>
                      <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500 mb-1">
                        Legal Hold
                      </div>
                      <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={!!draft.legal_hold}
                          onChange={(e) => setField("legal_hold", e.target.checked)}
                          data-testid="edit-legal-hold" />
                        {draft.legal_hold ? "Yes — asset held" : "No"}
                      </label>
                    </div>
                    <div className="col-span-2">
                      <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500 mb-1">
                        Notes
                      </div>
                      <Textarea rows={2} value={draft.notes || ""}
                        onChange={(e) => setField("notes", e.target.value)}
                        placeholder="Internal notes visible to the operations team."
                        data-testid="edit-notes" />
                    </div>
                    {(draft.serial !== asset.serial
                      || draft.asset_tag !== asset.asset_tag) && (
                      <div className="col-span-2">
                        <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-amber-800 mb-1">
                          Reason for identifier correction (required)
                        </div>
                        <Input value={draft.change_reason || ""}
                          onChange={(e) => setField("change_reason", e.target.value)}
                          placeholder="e.g. Corrected transposed digits from label"
                          className="border-amber-300 focus-visible:ring-amber-500"
                          data-testid="edit-change-reason" />
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <Field label="Master Company" value={asset.master_company_id ? asset.master_company_id.slice(0, 8) : "—"} />
                    <Field label="Company" value={asset.company?.name} />
                    <Field label="Ownership" value={asset.ownership_type} />
                    <Field label="Country" value={asset.country} />
                    <Field label="Current Employee" value={asset.current_employee?.name} />
                    <Field label="Department" value={asset.current_employee?.department} testId="view-department" />
                    <Field label="Location" value={x360?.header?.location} testId="view-location" />
                    <Field label="Device Type" value={asset.category} testId="view-device-type" />
                    <Field label="Warehouse" value={asset.warehouse?.name} />
                    <Field label="MDM" value={asset.mdm_status} testId="view-mdm-status" />
                    <Field label="Enrollment" value={asset.enrollment_status} testId="view-enrollment-status" />
                    <Field label="Data Erasure" value={asset.data_erasure_status} testId="view-data-erasure-status" />
                    <Field label="Legal Hold" value={asset.legal_hold ? "Yes" : "No"} testId="view-legal-hold" />
                    {asset.notes && (
                      <div className="col-span-2">
                        <Field label="Notes" value={asset.notes} testId="view-notes" />
                      </div>
                    )}
                  </>
                )}
              </TabsContent>

              <TabsContent value="specs" className="mt-4 grid grid-cols-2 gap-4">
                {editing ? (
                  <>
                    {SPEC_FIELDS.slice(0, 4).map(([k, label]) => (
                      <EditableField key={k} label={label} value={draft[k] ?? asset[k]} onChange={(v) => setField(k, v)} testId={`asset-edit-${k}`} />
                    ))}
                    <EditableField label="Config" multiline className="col-span-2" value={draft.config ?? asset.config} onChange={(v) => setField("config", v)} testId="asset-edit-config"
                      placeholder="Full configuration — e.g. Apple 11-inch iPad Wi-Fi 256GB | Chip: A16 | Colour: Silver" />
                    {SPEC_FIELDS.slice(4).map(([k, label]) => (
                      <EditableField key={k} label={label} value={draft[k] ?? asset[k]} onChange={(v) => setField(k, v)} testId={`asset-edit-${k}`} />
                    ))}
                  </>
                ) : (
                  <>
                    <Field label="Brand" value={asset.brand} />
                    <Field label="Category" value={asset.category} />
                    <Field label="Model" value={asset.model} />
                    <Field label="SKU" value={asset.sku} />
                    <Field label="Config" value={asset.config} />
                    <Field label="OS" value={asset.os} />
                    <Field label="Colour" value={asset.colour} />
                    <Field label="Processor" value={asset.processor} />
                    <Field label="RAM" value={asset.ram} />
                    <Field label="Storage" value={asset.storage} />
                    <Field label="IMEI 1" value={asset.imei1} />
                    <Field label="IMEI 2" value={asset.imei2} />
                  </>
                )}
              </TabsContent>

              <TabsContent value="warranty" className="mt-4 grid grid-cols-2 gap-4">
                {editing ? (
                  <>
                    <EditableField label="Purchase Date" value={draft.purchase_date}
                      onChange={(v) => setField("purchase_date", v)} type="date"
                      testId="edit-purchase-date" />
                    <EditableField label="PO Number" value={draft.po_number}
                      onChange={(v) => setField("po_number", v)}
                      testId="edit-po-number" />
                    <EditableField label="Invoice Number" value={draft.invoice_number}
                      onChange={(v) => setField("invoice_number", v)}
                      testId="edit-invoice-number" />
                    <EditableField label="Invoice Date" value={draft.invoice_date}
                      onChange={(v) => setField("invoice_date", v)} type="date"
                      testId="edit-invoice-date" />
                    <EditableField label="Purchase Price" value={draft.purchase_price}
                      onChange={(v) => setField("purchase_price", v)} type="number"
                      testId="edit-purchase-price" />
                    <EditableField label="Currency" value={draft.currency}
                      onChange={(v) => setField("currency", v)}
                      placeholder="USD"
                      testId="edit-currency" />
                    <EditableField label="Supplier" value={draft.supplier}
                      onChange={(v) => setField("supplier", v)}
                      testId="edit-supplier" />
                    <EditableField label="ABM ID" value={draft.abm_id}
                      onChange={(v) => setField("abm_id", v)}
                      testId="edit-abm-id" />
                    <EditableField label="Warranty Start" value={draft.warranty_start}
                      onChange={(v) => setField("warranty_start", v)} type="date"
                      testId="edit-warranty-start" />
                    <EditableField label="Warranty End" value={draft.warranty_end}
                      onChange={(v) => setField("warranty_end", v)} type="date"
                      testId="edit-warranty-end" />
                    <EditableField label="AppleCare+ End" value={draft.applecare_end}
                      onChange={(v) => setField("applecare_end", v)} type="date"
                      testId="edit-applecare-end" />
                  </>
                ) : (
                  <>
                    <Field label="Purchase Date" value={asset.purchase_date} testId="view-purchase-date" />
                    <Field label="PO Number" value={asset.po_number} testId="view-po-number" />
                    <Field label="Invoice" value={asset.invoice_number} testId="view-invoice-number" />
                    <Field label="Purchase Price" value={asset.purchase_price ? `${asset.currency || "USD"} ${asset.purchase_price}` : "—"} testId="view-purchase-price" />
                    <Field label="Warranty Start" value={asset.warranty_start} testId="view-warranty-start" />
                    <Field label="Warranty End" value={asset.warranty_end} testId="view-warranty-end" />
                    <Field label="AppleCare+ End" value={asset.applecare_end} testId="view-applecare-end" />
                    <Field label="Supplier" value={asset.supplier} testId="view-supplier" />
                    <Field label="ABM ID" value={asset.abm_id} testId="view-abm-id" />
                  </>
                )}
                <div className="col-span-2">
                  <div className="mb-2 text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500">Warranty Intelligence — all coverages</div>
                  <AssetWarrantyPanel assetId={assetId} canEdit={!isDisposed} onChanged={() => { load360(); onChanged?.(); }} />
                </div>
                <div className="col-span-2">
                  <AssetEhsCoverage assetId={assetId} serial={asset.serial} />
                </div>
              </TabsContent>

              <TabsContent value="history" className="mt-4 space-y-6">
                <HistorySection title="Shipments" items={history.shipments} render={(s) => `${s.shipment_code} · ${s.status} · ${s.courier || ""} ${s.awb || ""}`} />
                <HistorySection title="Reverse Pickups" items={history.pickups} render={(p) => `${p.pickup_code} · ${p.status}`} />
                <HistorySection title="Service Cases" items={history.services} render={(s) => `${s.case_code} · ${s.status} · ${s.problem}`} />
                <HistorySection title="Inspections" items={history.inspections} render={(i) => `Grade ${i.condition_grade} · ${new Date(i.created_at).toLocaleDateString()}`} />
                <HistorySection title="Data Erasures" items={history.erasures} render={(e) => `${e.certificate_number} · ${e.method} · ${e.result || "pending"}`} />
                <HistorySection title="ITAD Records" items={history.itad} render={(i) => `${i.itad_code} · ${i.disposition} · ${i.status}`} />
              </TabsContent>

              <TabsContent value="timeline" className="mt-4">
                <Timeline items={events} />
              </TabsContent>

              <TabsContent value="preflight" className="mt-4">
                <PreflightPanel entityKind="asset" entityId={asset.id} stage="submit" />
              </TabsContent>
              <TabsContent value="health" className="mt-4">
                <AssetHealthTab assetId={asset.id} />
              </TabsContent>
              {x360 && (
                <>
                  <TabsContent value="hardware" className="mt-4">
                    <HealthCategorySection health={x360.health} keys={["hardware", "performance"]} title="Hardware health" testId="asset-section-hardware"
                      extra={<Section title="Hardware"><div className="grid grid-cols-2 gap-3 sm:grid-cols-4"><Field label="Processor" value={asset.processor} /><Field label="RAM" value={asset.ram} /><Field label="Storage" value={asset.storage} /><Field label="Condition" value={asset.condition} /></div></Section>} />
                  </TabsContent>
                  <TabsContent value="software" className="mt-4">
                    <HealthCategorySection health={x360.health} keys={["os"]} title="Software" testId="asset-section-software"
                      extra={<Section title="Software"><div className="grid grid-cols-2 gap-3 sm:grid-cols-4"><Field label="Operating System" value={asset.os} /><Field label="Config" value={asset.config} /><Field label="MDM" value={asset.mdm_status} /><Field label="Enrollment" value={asset.enrollment_status} /></div></Section>} />
                  </TabsContent>
                  <TabsContent value="security" className="mt-4">
                    <HealthCategorySection health={x360.health} keys={["security"]} title="Security" testId="asset-section-security"
                      extra={<Section title="Security posture"><div className="grid grid-cols-2 gap-3 sm:grid-cols-4"><Field label="Legal Hold" value={asset.legal_hold ? "Yes" : "No"} /><Field label="Data Erasure" value={asset.data_erasure_status} /><Field label="ABM ID" value={asset.abm_id} /><Field label="Open alerts" value={String(x360.tickets.open_alerts.length)} /></div></Section>} />
                  </TabsContent>
                  <TabsContent value="compliance" className="mt-4"><ComplianceSection x={x360} /></TabsContent>
                  <TabsContent value="tickets" className="mt-4"><TicketsSection t={x360.tickets} /></TabsContent>
                  <TabsContent value="repairs" className="mt-4"><RepairHistorySection items={x360.repair_history} /></TabsContent>
                  <TabsContent value="assignments" className="mt-4"><AssignmentHistorySection items={x360.assignment_history} current={x360.asset.current_employee} /></TabsContent>
                  <TabsContent value="lifecycle" className="mt-4"><div className="space-y-3"><AssetLifecycleCard assetId={asset.id} /><LifecycleSection l={x360.lifecycle} /></div></TabsContent>
                  <TabsContent value="financial" className="mt-4"><FinancialSection f={x360.financial} /></TabsContent>
                  <TabsContent value="buyback" className="mt-4"><BuybackSection b={x360.buyback} /></TabsContent>
                  <TabsContent value="documents" className="mt-4"><DocumentsSection d={x360.documents} /></TabsContent>
                  <TabsContent value="audit" className="mt-4"><AuditTrailSection rows={x360.audit_trail} events={x360.events} /></TabsContent>
                </>
              )}
            </Tabs>

            <AppleBusinessAssetSection asset={asset} />


            <div className="mt-6">
              <RecordStickyNotes
                contextType="asset"
                contextId={asset.id}
                masterCompanyId={asset.master_company_id}
                title="Notes on this asset"
              />
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function HistorySection({ title, items, render }) {
  if (!items?.length) {
    return (
      <div>
        <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500 mb-2">{title}</div>
        <div className="text-xs text-slate-400 italic">None</div>
      </div>
    );
  }
  return (
    <div>
      <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500 mb-2">{title}</div>
      <ul className="space-y-1">
        {items.map((it, i) => (
          <li key={it.id || i} className="text-sm text-slate-700 font-mono bg-slate-50 border border-slate-100 rounded px-2 py-1">
            {render(it)}
          </li>
        ))}
      </ul>
    </div>
  );
}

function AppleBusinessAssetSection({ asset }) {
  const has = asset?.apple_business_status || asset?.apple_part_number || asset?.source === "APPLE_BUSINESS";
  if (!has) return null;
  const ac = asset.apple_care || {};
  const conflicts = asset.apple_conflicts || [];
  const fmtD = (s) => (s ? new Date(s).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—");
  return (
    <div className="mt-6 rounded-lg border border-slate-200 bg-slate-50/60 p-4" data-testid="asset-apple-business-section">
      <div className="mb-3 flex items-center gap-2">
        <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-700">Apple Business</div>
        {asset.apple_business_status && (
          <span className={`rounded px-1.5 py-0.5 text-[9px] font-semibold ${asset.apple_business_status === "APPLE_BUSINESS_RELEASED" ? "bg-slate-200 text-slate-500" : "bg-emerald-100 text-emerald-700"}`}>
            {asset.apple_business_status.replace(/_/g, " ")}
          </span>
        )}
        {ac.status === "ACTIVE" && <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[9px] font-semibold text-emerald-700">APPLECARE ACTIVE</span>}
        {conflicts.map((c) => (
          <span key={c} className="rounded bg-rose-100 px-1.5 py-0.5 text-[9px] font-semibold text-rose-700" data-testid={`asset-apple-conflict-${c}`}>{c.replace(/_/g, " ")} — review</span>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Field label="Apple Part Number" value={asset.apple_part_number} testId="apple-part-number" />
        <Field label="Colour" value={asset.apple_colour} />
        <Field label="Capacity" value={asset.apple_capacity} />
        <Field label="Order Number" value={asset.apple_order_number} />
        <Field label="Apple Order Date" value={fmtD(asset.apple_order_date)} testId="apple-order-date" />
        <Field label="Purchase Source" value={asset.apple_purchase_source} />
        <Field label="AppleCare Coverage" value={ac.description} />
        <Field label="AppleCare Start" value={fmtD(ac.startDate)} />
        <Field label="AppleCare End" value={fmtD(ac.endDate)} />
      </div>
      {conflicts.length > 0 && (
        <div className="mt-2 text-[10px] text-slate-500">Apple values are stored as evidence; verified ONE values were not overwritten.</div>
      )}
    </div>
  );
}

const COV_CHIP = {
  QUOTED: "bg-slate-100 text-slate-600 border-slate-200",
  ORDERED: "bg-blue-50 text-blue-700 border-blue-200",
  PURCHASED: "bg-blue-50 text-blue-700 border-blue-200",
  ACTIVATION_PENDING: "bg-amber-50 text-amber-700 border-amber-200",
  ACTIVE: "bg-emerald-50 text-emerald-700 border-emerald-200",
  EXPIRED: "bg-slate-100 text-slate-500 border-slate-200",
  CANCELLED: "bg-red-50 text-red-700 border-red-200",
};

function AssetEhsCoverage({ assetId, serial }) {
  const [items, setItems] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ plan_part_number: "", coverage_start: "", reference_number: "" });

  const load = useCallback(() => api.get(`/warranty-plans/coverage/asset/${assetId}`)
    .then((r) => setItems(r.data.items || [])).catch(() => {}), [assetId]);
  useEffect(() => { if (assetId) load(); /* eslint-disable-next-line */ }, [assetId, load]);

  const save = async () => {
    if (!form.plan_part_number.trim()) { toast.error("Enter the EHS Plus part number"); return; }
    try {
      await api.post("/warranty-plans/coverage", { asset_id: assetId, serial,
        plan_part_number: form.plan_part_number.trim(), coverage_start: form.coverage_start || null,
        reference_number: form.reference_number || null, status: "ACTIVATION_PENDING" });
      toast.success("Coverage recorded (activation pending)");
      setAdding(false); setForm({ plan_part_number: "", coverage_start: "", reference_number: "" }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not record coverage"); }
  };

  const setStatus = async (id, status) => {
    try { await api.patch(`/warranty-plans/coverage/${id}`, { status }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="mt-4 rounded-lg border border-sky-200 bg-sky-50/40 p-3" data-testid="asset-ehs-coverage">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[13px] font-semibold text-sky-800">Microsoft EHS Plus coverage</div>
        <Button size="sm" variant="outline" className="h-7" onClick={() => setAdding((a) => !a)}
          data-testid="ehs-add-toggle">{adding ? "Cancel" : "Record coverage"}</Button>
      </div>
      {adding && (
        <div className="grid grid-cols-3 gap-2 mb-2" data-testid="ehs-add-form">
          <Input placeholder="Plan part # (e.g. NRI-00144)" value={form.plan_part_number}
            onChange={(e) => setForm({ ...form, plan_part_number: e.target.value })} className="h-8 text-xs" data-testid="ehs-part" />
          <Input type="date" value={form.coverage_start}
            onChange={(e) => setForm({ ...form, coverage_start: e.target.value })} className="h-8 text-xs" data-testid="ehs-start" />
          <div className="flex gap-1">
            <Input placeholder="Ref / agreement" value={form.reference_number}
              onChange={(e) => setForm({ ...form, reference_number: e.target.value })} className="h-8 text-xs" />
            <Button size="sm" className="h-8 bg-sky-600 hover:bg-sky-700 text-white" onClick={save} data-testid="ehs-save">Save</Button>
          </div>
        </div>
      )}
      {items.length === 0 ? (
        <div className="text-[12px] text-slate-400" data-testid="ehs-empty">No EHS Plus coverage recorded for this asset.</div>
      ) : items.map((c) => (
        <div key={c.id} className="flex items-center gap-2 py-1.5 border-t border-sky-100 text-[12px]" data-testid={`ehs-cov-${c.id}`}>
          <div className="min-w-0 flex-1">
            <div className="text-slate-800 truncate">{c.plan_name}</div>
            <div className="text-[11px] text-slate-400 font-mono">
              {c.plan_part_number} · {c.total_years}yr · {c.coverage_start || "?"} → {c.coverage_end || "?"}
              {c.reference_number ? ` · ${c.reference_number}` : ""}
            </div>
          </div>
          <span className={"text-[10px] px-1.5 py-0.5 rounded border " + (COV_CHIP[c.status] || COV_CHIP.QUOTED)}>
            {(c.status || "").replace(/_/g, " ")}
          </span>
          {c.status !== "ACTIVE" && c.status !== "EXPIRED" && c.status !== "CANCELLED" && (
            <Button size="sm" variant="outline" className="h-6 text-[11px] px-2"
              onClick={() => setStatus(c.id, "ACTIVE")} data-testid={`ehs-activate-${c.id}`}>Mark active</Button>
          )}
        </div>
      ))}
      <div className="text-[10px] text-slate-400 mt-1.5">India-only national warranty · accidental damage not covered</div>
    </div>
  );
}

